/** In-memory access token — never persisted to localStorage/sessionStorage. */
let accessToken: string | null = null;

/**
 * Notified whenever the token appears or is cleared.
 *
 * Exists because "we are now signed in" happens in three different places —
 * login, session restore from the refresh cookie on a cold load, and a token
 * rotation during refresh — and anything that must run when it happens (FCM
 * registration) was previously wired to login ONLY, so a vendor who kept a
 * long-lived session never re-registered and silently stopped getting pushes.
 * One signal covers all three rather than three call sites that drift apart.
 */
type AccessTokenListener = (token: string | null) => void;
const listeners = new Set<AccessTokenListener>();

function notify(token: string | null): void {
  for (const listener of listeners) {
    try {
      listener(token);
    } catch {
      // A listener must never break session setup.
    }
  }
}

export const accessTokenStore = {
  get(): string | null {
    return accessToken;
  },
  set(token: string | null): void {
    accessToken = token?.trim() || null;
    notify(accessToken);
  },
  clear(): void {
    accessToken = null;
    notify(null);
  },
  /** Subscribe to sign-in/sign-out. Returns an unsubscribe function. */
  onChange(listener: AccessTokenListener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
